/**
 * One handler per graph command (§5.3), with the planner validator inlined (§7.1). Handlers run inside a changeset
 * fold: they validate against the scratch state, write every default into the event, and emit. Items: decision 0035.
 */

import type { ItemRouting } from "@minga/protocol"
import { DEFAULT_ITEM_TYPE, ITEM_TYPES, isPlannableType, routeAgents, typeDefOf } from "@minga/protocol"
import { mayManage } from "../authorize"
import { levelOf } from "../autonomy"
import { BRIEF_MAX_CHARS, briefHash } from "../brief"
import { canonicalJson } from "../hash"
import { timedCapOf } from "../monitors"
import {
  isWorkingMember,
  MINGA_ONLY_PATCH_KEYS,
  shapingBefore,
  shapingPatch,
  workspaceOwnerOf,
} from "../multiplayer"
import { pageOf } from "../pages"
import { agentAboveOf } from "../people"
import { mergePlacement } from "../placement"
import { PolicyPresets } from "../policy"
import { openOccurrenceOf, resolvePredecessor } from "../readiness"
import { cronProblem, isTimeZone, occurrenceTitle, periodOf } from "../recurrence"
import { resolveRouting, routingChoicePlacement, validatorRequired } from "../routing"
import {
  checksEvidenceGap,
  closeByPerson,
  doneEvidence,
  enterCurrentStage,
  holdForGate,
  inReviewNextStep,
  openGoGate,
  passStage,
  personsCloseBy,
  remapReviewStage,
  replaceUnstarted,
  reviewStageOf,
  withdrawEscalations,
  withdrawReviewCards,
} from "../runs"
import {
  agentOf,
  areaOf,
  edgeOf,
  goalOf,
  isClosed,
  isOpenItem,
  isRunEnded,
  itemOf,
  kpiOf,
  leadOf,
  must,
  principalOf,
  runOf,
} from "../state"
import { childrenOf, depthOf, isContainer, isPlannable, MAX_DEPTH, openDescendants } from "../tree"
import type {
  Agent,
  Assignee,
  Bet,
  GraphCommand,
  Hands,
  Ids,
  Item,
  ItemId,
  Principal,
  Recurrence,
  Resolution,
  Review,
  ReviewConfig,
} from "../types"
import {
  blocksCycle,
  cycleMessage,
  holdViolation,
  parentCycle,
  powersViolation,
  workExists,
} from "../validate"
import { ownerLeft, presetOf } from "../workers"
import { emitShaping, mayPassEscalation, mayResume } from "./items"
import { beforeOf, forbidden, invalid, maxRank, notFound, rankAfter, reject, type Tx } from "./tx"

const same = (a: unknown, b: unknown): boolean => canonicalJson(a) === canonicalJson(b)

const exists = <T>(xs: ReadonlyArray<T & { readonly id: string }>, id: string) => xs.some((x) => x.id === id)

const requireNew = (tx: Tx, id: string, what: string) => {
  const s = tx.state
  const all = [
    s.goals,
    s.kpis,
    s.areas,
    s.agents,
    s.items,
    s.edges,
    s.policies,
    s.proposals,
  ] as ReadonlyArray<ReadonlyArray<{ readonly id: string }>>
  if (all.some((xs) => exists(xs, id))) invalid(`${what} id ${id} already exists`, "id")
}

const checkHold = (
  tx: Tx,
  hold: { readonly _tag: string } & Parameters<typeof holdViolation>[1],
  kind: "edit" | "move",
) => {
  const v = holdViolation(tx.actor, hold, kind)
  if (v) forbidden("held", v)
}

/** Planner rule: every goal and item carries a summary of why it exists (§7.1). */
const requireWhy = (why: string | undefined, path: string) => {
  if (why !== undefined && why.trim().length === 0)
    invalid("say why this exists: `why` must not be blank", path)
}

const activeArea = (tx: Tx, id: Ids["AreaId"], path = "areaId") => {
  const a = areaOf(tx.state, id) ?? notFound({ _tag: "area", id })
  if (a.status !== "active") invalid("area is archived", path)
  return a
}

const liveAgent = (tx: Tx, id: Ids["AgentId"]) => {
  const a = agentOf(tx.state, id) ?? notFound({ _tag: "agent", id })
  if (a.status === "retired") invalid(`agent ${a.handle} is retired`, "agentId")
  return a
}

/**
 * Bet fields with their own command or owner (§6): the success condition (ChangeTarget, change_kpi_target), the
 * verdict (RecordBetVerdict, never by the bet's owner), and the kill conditions and appetite (people). An agent's
 * UpdateItem may reshape a bet's hypothesis and framing, not these; nobody writes a verdict through a patch.
 */
const checkBetPatch = (tx: Tx, cur: Bet | null, next: Bet | null) => {
  if (cur === null) {
    if (next !== null && !kpiOf(tx.state, next.inputKpiId))
      invalid("a bet must name an existing input KPI", "patch.bet.inputKpiId")
    if (next?.verdict)
      invalid("a bet's verdict is recorded with RecordBetVerdict, not a patch", "patch.bet.verdict")
    return
  }
  if (next === null) {
    if (tx.actor._tag !== "user") invalid("an agent cannot drop a bet", "patch.bet")
    return
  }
  if (!same(next.verdict, cur.verdict))
    invalid("a bet's verdict is recorded with RecordBetVerdict, not a patch", "patch.bet.verdict")
  if (tx.actor._tag === "user") return
  if (!same(next.success, cur.success))
    invalid("change a bet's success condition with ChangeTarget (change_kpi_target)", "patch.bet.success")
  if (!same(next.kill, cur.kill)) invalid("a bet's kill conditions are changed by a person", "patch.bet.kill")
  if (!same(next.appetite, cur.appetite))
    invalid("a bet's appetite is changed by a person", "patch.bet.appetite")
  if (next.inputKpiId !== cur.inputKpiId || next.laggingKpiId !== cur.laggingKpiId)
    invalid("a bet's KPIs are changed by a person", "patch.bet.inputKpiId")
}

const activeAgent = (tx: Tx, id: Ids["AgentId"], path: string): Agent => {
  const a = agentOf(tx.state, id)
  if (!a) invalid(`agent ${id} does not exist`, path)
  if (a.status === "retired") invalid(`agent ${a.handle} is retired`, path)
  return a
}

/**
 * An owner (decision 0035): an agent owner is active, not the Mayor, and a role agent of the item's area, a
 * project-wide role agent or the lead; a person owner is a member and not a viewer.
 */
const validateOwner = (tx: Tx, owner: Principal | null, areaId: Ids["AreaId"] | null, path: string) => {
  if (owner === null) return
  if (owner._tag === "user") {
    const m = tx.state.members.find((x) => x.userId === owner.userId)
    if (!m) invalid("the owner must be a member of the workspace", path)
    if (m.role === "viewer") invalid(`@${m.handle} is a viewer and can't own items`, path)
    return
  }
  const a = activeAgent(tx, owner.agentId, path)
  if (a.role === "mayor" || tx.state.mayor?.agentId === a.id) invalid("the Mayor never owns an item", path)
  // K7: a project-wide role agent (no area) may own items of any area
  if (a.role === "role" && a.areaId !== null && areaId !== null && a.areaId !== areaId)
    invalid(`@${a.handle} is not an agent of this item's area`, path)
}

/**
 * An assignee (decision 0035, note 7): a person (any member), or a new worker for the owner agent (needs an agent
 * owner; a preset of the project). The owner agent itself is reserved this release (D13).
 */
const validateAssignee = (tx: Tx, assignee: Assignee | null, owner: Principal | null, path: string) => {
  if (assignee === null) return
  switch (assignee._tag) {
    case "agent":
      invalid("a persistent agent can't do items itself yet; use a worker", path)
      break
    case "worker":
      if (owner?._tag !== "agent") invalid("make an agent the owner first: it supervises the worker", path)
      if (assignee.preset !== null && !presetOf(tx.state, assignee.preset))
        invalid(`no worker preset ${assignee.preset} in this project`, `${path}.preset`)
      break
    case "user": {
      const m = tx.state.members.find((x) => x.userId === assignee.userId)
      if (!m) invalid("the assignee must be a member of the workspace", path)
      if (m.role === "viewer") invalid(`@${m.handle} is a viewer and can't be assigned items`, path)
      break
    }
  }
}

/** The assignee an item gets when none is named: none for plannable types, the person, or a new worker. */
const defaultAssignee = (type: string, owner: Principal | null): Assignee | null =>
  isPlannableType(type)
    ? null
    : owner?._tag === "user"
      ? { _tag: "user", userId: owner.userId }
      : owner?._tag === "agent"
        ? { _tag: "worker", preset: null }
        : null

const samePrincipal = (a: Principal | null, b: Principal | null): boolean =>
  a === null || b === null ? a === b : same(a, b)

/**
 * Decision 0154 (review 10-23 #5): a worker item created with a required validator route gets the validator stage
 * (first, as for code items) when its stages lack one, so "required" never silently means "no validator ran". Only at
 * creation: a later route change on an item without the stage is a plan warning (it changes checks, a person's call
 * once they froze).
 */
const withRequiredValidator = (review: Review, required: boolean): Review =>
  required && !review.stages.includes("validator")
    ? { ...review, stages: ["validator", ...review.stages] as unknown as Review["stages"] }
    : review

const defaultReview = (
  checks: ReadonlyArray<Item["checks"][number]>,
  cfg?: ReviewConfig | Review,
): Review => {
  const stages = [...(cfg?.stages ?? (["agent"] as const))]
  // Decision 0074: human review is selected rather than implied by publication.
  if (checks.some((c) => c._tag === "human") && !stages.includes("human")) stages.push("human")
  return {
    stages: stages as unknown as Review["stages"],
    crossModel: cfg?.crossModel ?? true,
    maxRounds: cfg?.maxRounds ?? 3,
    // null = the item's accountable person, resolved when someone is needed
    escalateTo: cfg?.escalateTo ?? null,
    round: 0,
    stage: 0,
  }
}

/** A new occurrence starts its own person-round count (decision 0050). */
const withoutPersonRounds = ({ personRounds: _, ...r }: Item["review"]): Item["review"] => r

/** Rank after the siblings: the same parent, or the same area's top-level items. */
const siblingsRank = (tx: Tx, parentId: ItemId | null, areaId: Ids["AreaId"] | null): string =>
  rankAfter(
    maxRank(
      tx.state.items
        .filter((t) => t.parentId === parentId && (parentId !== null || t.areaId === areaId))
        .map((t) => t.rank),
    ),
  )

/**
 * Cancel an item and its open descendants (§7.2 cascade); request cancellation of their live runs. The item gets
 * `resolution`; the descendants `wont_do` with "parent cancelled: …".
 */
export const cancelCascade = (
  tx: Tx,
  item: Item,
  reason: string,
  resolution: Resolution = { _tag: "wont_do" },
) => {
  for (const t of [item, ...openDescendants(tx.state, item.id)]) {
    const cur = itemOf(tx.state, t.id)
    if (!cur || !isOpenItem(cur)) continue
    // decision 0050: a cancelled item's review card never stays open with its picks
    withdrawReviewCards(tx, cur.id)
    if (cur.activeRunId) {
      const run = tx.state.runs.find((r) => r.id === cur.activeRunId)
      if (run && !isRunEnded(run))
        tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason, requestedAt: tx.now })
    }
    const top = t.id === item.id
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId: cur.id,
      from: cur.status,
      to: "cancelled",
      cause: { _tag: "cancelled", reason: top ? reason : `parent cancelled: ${reason}` },
      blocked: null,
      resolution: top ? resolution : { _tag: "wont_do" },
    })
  }
}

/**
 * Wave F (decision 0062): who asked for a new item. A person: that person. An agent in a turn, or a worker: the asker
 * of that run (the turn is live while it creates items, so it is in state). Anyone else: absent.
 */
const askedByOfCreator = (tx: Tx): { askedBy?: Principal } => {
  const a = tx.actor
  if (a._tag === "user") return { askedBy: { _tag: "user", userId: a.userId } }
  if (a._tag === "agent" || a._tag === "worker") {
    const asked = runOf(tx.state, a.runId)?.askedBy
    return asked ? { askedBy: asked } : {}
  }
  return {}
}

const requireItem = (tx: Tx, id: ItemId): Item => itemOf(tx.state, id) ?? notFound({ _tag: "item", id })

/**
 * Decision 0152: re-filing a closed item must not change what runs. A done child turns an open leaf into a container
 * (its own worker would never start) and its last done child leaving turns an open container back into a leaf (its
 * worker would start). Plannable items (release, bet, workstream) never run a worker, so they take and lose children
 * freely; a cancelled child counts for neither.
 */
const refileGuards = (tx: Tx, t: Item, parentId: ItemId | null): void => {
  if (t.status === "cancelled") return
  const s = tx.state
  const to = parentId ? itemOf(s, parentId) : undefined
  if (to && isOpenItem(to) && !isPlannable(to) && !isContainer(s, to))
    invalid(
      `${to.ref} is an open ${to.type} with work of its own: filing ${t.ref} under it would make it a container. File finished work under a workstream, release or bet`,
      "parentId",
    )
  const from = t.parentId ? itemOf(s, t.parentId) : undefined
  if (
    from &&
    isOpenItem(from) &&
    !isPlannable(from) &&
    !childrenOf(s, from.id).some((c) => c.id !== t.id && c.status !== "cancelled")
  )
    invalid(
      `${t.ref} is the last child of ${from.ref}, an open ${from.type}: without it ${from.ref} would run as a leaf. Close ${from.ref} first, or file it too`,
      "parentId",
    )
}

/** Wave E (spec §3.2): the UpdateItem keys that change what the next attempt reads or does (they shape the item). */
const SHAPING_KEYS = [
  "title",
  "body",
  "why",
  "hands",
  "routing",
  "assignee",
  "areaId",
  "integrations",
  "placement",
  "kind",
] as const

/** K4: a model or effort in `hands` names its CLI family (only for a `hands` value present in the command). */
const checkHands = (hands: Hands | undefined, path: string) => {
  if (hands === undefined || hands.agent !== null) return
  if (hands.model !== null || hands.effort !== null)
    invalid("say which CLI the model is for: hands.agent", `${path}.agent`)
}

export const checkRouting = (
  tx: Tx,
  routing: typeof ItemRouting.Type | null | undefined,
  itemType: string,
  path: string,
  hands: Hands | undefined,
) => {
  for (const kind of ["worker", "validator"] as const) {
    const choice = routing?.[kind]
    if (!choice) continue
    const policy = resolveRouting(tx.state.project?.routing, { kind, itemType })
    const placement = mergePlacement(policy.placement, routingChoicePlacement(choice), null)
    if ("invalid" in placement) invalid(placement.invalid, `${path}.${kind}`)
    if (choice.mode === "required") {
      // decision 0118: routes allow one or several CLIs; a conflict is an explicit pin outside the choice's set
      const chosen = routeAgents(choice)
      const ruleSet = policy.required ? routeAgents(policy.rule) : []
      const pins = ruleSet.length > 0 ? ruleSet : hands?.agent ? [hands.agent] : []
      if (chosen.length > 0 && pins.length > 0 && !chosen.some((a) => pins.includes(a)))
        invalid("task route conflicts with the explicit CLI pin", `${path}.${kind}.agent`)
      const families =
        chosen.length === 0 ? pins : pins.length === 0 ? chosen : chosen.filter((a) => pins.includes(a))
      for (const family of families)
        for (const field of ["model", "effort"] as const) {
          const pinned =
            (policy.required ? policy.byAgent[family]?.[field] : null) ??
            (hands?.agent === family ? hands[field] : null)
          const selected = choice.byAgent[family]?.[field]
          if (pinned && selected && pinned !== selected)
            invalid(
              `task route conflicts with the explicit ${field} pin`,
              `${path}.${kind}.byAgent.${family}.${field}`,
            )
        }
    }
    if (policy.required && choice.mode === "required") {
      for (const family of ["claude", "codex", "antigravity", "muse"] as const)
        for (const field of ["model", "effort"] as const) {
          const required = policy.byAgent[family]?.[field]
          const requested = choice.byAgent[family]?.[field]
          if (required && requested && required !== requested)
            invalid(`required workflow ${field} is ${required}`, `${path}.${kind}.byAgent.${family}.${field}`)
        }
    }
  }
}

const checkRecurrence = (r: Recurrence, path: string) => {
  const problem = cronProblem(r.schedule.cron)
  if (problem) invalid(problem, `${path}.schedule.cron`)
  if (!isTimeZone(r.schedule.tz))
    invalid(`unknown time zone ${r.schedule.tz} (use an IANA name)`, `${path}.schedule.tz`)
  if (r.window !== null && r.window <= 0) invalid("an occurrence's window is positive (ms)", `${path}.window`)
}

/**
 * Decision 0126 §7 (0127): the person whose rights a change is checked against. The acting person; or, for an agent's
 * change applied on a card, the person who approved it (an approval never lends an agent's reach to a member); null
 * for an agent acting on its own or on another agent's decision (its policy and the chain govern it).
 */
export const rightsHolderOf = (tx: Tx): Ids["UserId"] | null => {
  const a = tx.actor
  if (a._tag === "user") return a.userId
  const by = tx.scope.approvedBy?.by
  return by?._tag === "user" ? by.userId : null
}

/**
 * Decision 0126 §2 (0127): hiring agents, areas and the brief are every project member's (not a viewer's), checked
 * against the acting person or the person who approved the agent's card.
 */
const personWorksInProject = (tx: Tx, what: string) => {
  const u = rightsHolderOf(tx)
  if (u !== null && !isWorkingMember(tx.state, u))
    forbidden("not_owner", `only a project member (not a viewer) may ${what}`)
}

/**
 * K3: who may change an agent. People: its owner or a workspace owner/admin. An agent acting for another person:
 * only on that person's approval (the verdict is forced to `propose`, see `crossOwnerTarget`); when the change is
 * applied on approval, the approver must still manage the target agent.
 */
const checkAgentChange = (tx: Tx, target: Agent) => {
  const a = tx.actor
  if (a._tag === "user") {
    if (!mayManage(tx.state, a.userId, target.ownerId))
      forbidden("not_owner", `only @${target.handle}'s owner or a workspace admin may change it`)
    return
  }
  if (a._tag !== "agent") return
  const by = tx.scope.approvedBy?.by
  if (!by) return
  if (by._tag !== "user" || !mayManage(tx.state, by.userId, target.ownerId))
    forbidden("not_owner", `a change to @${target.handle} needs its owner's approval`)
}

/** The agent a graph change targets when it changes an agent (K3). */
export const changedAgentOf = (s: Tx["state"], c: GraphCommand): Agent | undefined => {
  switch (c._tag) {
    case "UpdateAgent":
    case "ReviseAgentCharter":
    case "ChangeAgentMind":
    case "SetAgentStatus":
    case "TakeOverAgent":
      return agentOf(s, c.agentId)
    default:
      return undefined
  }
}

/** K3: the target agent of an agent's change when it belongs to someone other than the acting agent's principal. */
export const crossOwnerTarget = (s: Tx["state"], actor: Tx["actor"], c: GraphCommand): Agent | undefined => {
  if (actor._tag !== "agent") return undefined
  const target = changedAgentOf(s, c)
  return target && target.ownerId !== actor.onBehalfOf ? target : undefined
}

/** A handle is free among the project's live agents (other than `self`) and its members. */
const handleTaken = (tx: Tx, handle: string, self: Ids["AgentId"] | null): boolean =>
  tx.state.agents.some((a) => a.handle === handle && a.status !== "retired" && a.id !== self) ||
  tx.state.members.some((m) => m.handle === handle)

/** How many levels a subtree spans below its root (0 for a leaf). */
const heightOf = (tx: Tx, id: ItemId): number => {
  const kids = childrenOf(tx.state, id)
  return kids.length === 0 ? 0 : 1 + Math.max(...kids.map((k) => heightOf(tx, k.id)))
}

/**
 * Decision 0120: a ChecksRevised moved an item in review to another stage. The stage it left ends (a removed human
 * stage's card is withdrawn, a removed validator stage's runs that have not ended are cancelled) and the new one starts
 * (a human stage opens its card; an agent stage wakes the owner via reactions; a validator stage is dispatched).
 */
const leaveRemovedStage = (tx: Tx, before: Item, was: ReturnType<typeof reviewStageOf>): void => {
  const t = must(itemOf(tx.state, before.id), "item")
  if (reviewStageOf(t) === was) return
  if (was === "human") withdrawReviewCards(tx, t.id)
  if (was === "validator")
    for (const r of tx.state.runs)
      if (r.kind === "validator" && r.itemId === t.id && !isRunEnded(r) && r.cancelRequestedAt === undefined)
        tx.emit({
          _tag: "RunCancelRequested",
          runId: r.id,
          reason: `the validator stage of ${t.ref} was removed`,
          requestedAt: tx.now,
        })
  enterCurrentStage(tx, t.id)
}

/** The closer stands in for the agent review stage only when it is the owner, the lead, or a person on a person-owned item. */
const closerStandsIn = (tx: Tx, item: Item): boolean => {
  const a = tx.actor
  if (a._tag === "user") return item.owner?._tag === "user"
  if (a._tag === "agent")
    return (
      (item.owner?._tag === "agent" && item.owner.agentId === a.agentId) || leadOf(tx.state)?.id === a.agentId
    )
  return false
}

export const applyGraph = (tx: Tx, c: GraphCommand): void => {
  const s = () => tx.state
  const actor = tx.actor
  switch (c._tag) {
    // ---------------- goals & KPIs ----------------
    case "CreateGoal": {
      requireNew(tx, c.goalId, "goal")
      requireWhy(c.why, "why")
      for (const k of c.kpiIds ?? []) if (!kpiOf(s(), k)) invalid(`KPI ${k} does not exist`, "kpiIds")
      if (c.target && !kpiOf(s(), c.target.kpiId)) invalid("the target's KPI does not exist", "target.kpiId")
      tx.emit({
        _tag: "GoalCreated",
        goal: {
          ...tx.recordMeta(),
          id: c.goalId,
          ref: tx.nextRef("G"),
          title: c.title,
          why: c.why,
          body: c.body ?? "",
          rank: c.rank ?? rankAfter(maxRank(s().goals.map((g) => g.rank))),
          hold: { _tag: "none" },
          tags: [],
          status: "active",
          deadline: c.deadline ?? null,
          target: c.target ?? null,
          criteria: c.criteria ?? [],
          kpiIds: c.kpiIds ?? [],
        },
      })
      return
    }
    case "UpdateGoal": {
      const g = goalOf(s(), c.goalId) ?? notFound({ _tag: "goal", id: c.goalId })
      checkHold(tx, g.hold, "edit")
      requireWhy(c.patch.why, "patch.why")
      for (const k of c.patch.kpiIds ?? [])
        if (!kpiOf(s(), k)) invalid(`KPI ${k} does not exist`, "patch.kpiIds")
      tx.emit({ _tag: "GoalUpdated", goalId: g.id, patch: c.patch, before: beforeOf(g, c.patch) })
      return
    }
    case "SetGoalStatus": {
      const g = goalOf(s(), c.goalId) ?? notFound({ _tag: "goal", id: c.goalId })
      checkHold(tx, g.hold, "edit")
      if (g.status === c.status) invalid(`goal is already ${c.status}`, "status")
      tx.emit({ _tag: "GoalStatusChanged", goalId: g.id, status: c.status, before: g.status, note: c.note })
      return
    }
    case "ChangeTarget": {
      if (!kpiOf(s(), c.condition.kpiId)) invalid("the KPI does not exist", "condition.kpiId")
      if (c.subject._tag === "goal") {
        const g = goalOf(s(), c.subject.id) ?? notFound({ _tag: "goal", id: c.subject.id })
        checkHold(tx, g.hold, "edit")
        // a goal a person judges (target null) may get a target
        tx.emit({ _tag: "TargetChanged", subject: c.subject, condition: c.condition, before: g.target })
      } else {
        const i = requireItem(tx, c.subject.id)
        checkHold(tx, i.hold, "edit")
        if (i.bet === null) invalid("only bets have a target", "subject")
        tx.emit({
          _tag: "TargetChanged",
          subject: c.subject,
          condition: c.condition,
          before: i.bet.success.condition,
        })
      }
      return
    }
    case "DefineKpi": {
      requireNew(tx, c.kpiId, "KPI")
      if (s().kpis.some((k) => k.key === c.key)) invalid(`a KPI with key ${c.key} exists`, "key")
      if (c.areaId && !areaOf(s(), c.areaId)) invalid("area does not exist", "areaId")
      for (const k of c.rollsUpTo ?? []) if (!kpiOf(s(), k)) invalid(`KPI ${k} does not exist`, "rollsUpTo")
      tx.emit({
        _tag: "KpiDefined",
        kpi: {
          ...tx.recordMeta(),
          id: c.kpiId,
          ref: tx.nextRef("K"),
          areaId: c.areaId ?? null,
          key: c.key,
          name: c.name,
          unit: c.unit,
          direction: c.direction,
          role: c.role,
          source: c.source ?? { _tag: "manual" },
          cadence: c.cadence,
          latencyDays: c.latencyDays ?? 0,
          minReportable: c.minReportable ?? null,
          rollsUpTo: c.rollsUpTo ?? [],
          baseline: null,
          latest: null,
          ...(c.recipe ? { recipe: c.recipe } : {}),
        },
      })
      return
    }
    case "UpdateKpi": {
      const k = kpiOf(s(), c.kpiId) ?? notFound({ _tag: "kpi", id: c.kpiId })
      for (const r of c.patch.rollsUpTo ?? []) {
        if (r === k.id) invalid("a KPI cannot roll up to itself", "patch.rollsUpTo")
        if (!kpiOf(s(), r)) invalid(`KPI ${r} does not exist`, "patch.rollsUpTo")
      }
      tx.emit({
        _tag: "KpiUpdated",
        kpiId: k.id,
        patch: c.patch,
        before: beforeOf({ ...k, recipe: k.recipe ?? null }, c.patch),
      })
      return
    }
    // ---------------- areas & agents ----------------
    case "CreateArea": {
      personWorksInProject(tx, "change areas")
      requireNew(tx, c.areaId, "area")
      if (s().areas.some((a) => a.slug === c.slug && a.status === "active"))
        invalid(`an area with slug ${c.slug} exists`, "slug")
      if (c.policyId && !s().policies.some((p) => p.id === c.policyId))
        invalid("policy does not exist", "policyId")
      tx.emit({
        _tag: "AreaCreated",
        area: {
          ...tx.recordMeta(),
          id: c.areaId,
          slug: c.slug,
          name: c.name,
          color: c.color,
          rank: rankAfter(maxRank(s().areas.map((a) => a.rank))),
          charter: c.charter,
          intakeAgentId: null,
          policyId: c.policyId ?? null,
          grants: [],
          placement: c.placement ?? { requires: {}, prefers: [], exclusive: [] },
          powers: c.powers ?? { untrustedInput: false, privateData: false, externalEffects: false },
          wip: c.wip ?? { maxNow: 1 },
          interfaces: { publishes: [], consumes: [] },
          template: c.templateId ? { templateId: c.templateId, version: 1 } : null,
          status: "active",
        },
      })
      return
    }
    case "UpdateArea": {
      personWorksInProject(tx, "change areas")
      const a = areaOf(s(), c.areaId) ?? notFound({ _tag: "area", id: c.areaId })
      if (a.status !== "active") invalid("area is archived", "areaId")
      if (c.patch.intakeAgentId) {
        const ag = activeAgent(tx, c.patch.intakeAgentId, "patch.intakeAgentId")
        if (ag.role === "role" && ag.areaId !== null && ag.areaId !== a.id)
          invalid("the area's agent must belong to this area", "patch.intakeAgentId")
      }
      if (c.patch.wip && (!Number.isInteger(c.patch.wip.maxNow) || c.patch.wip.maxNow < 1))
        invalid("WIP: at least 1 Now item", "patch.wip.maxNow")
      if (c.patch.powers)
        for (const g of a.grants) {
          const v = powersViolation(c.patch.powers, g)
          if (v) invalid(`${v} (granted: ${g.integrationId})`, "patch.powers")
        }
      tx.emit({ _tag: "AreaUpdated", areaId: a.id, patch: c.patch, before: beforeOf(a, c.patch) })
      return
    }
    case "ReviseAreaCharter": {
      personWorksInProject(tx, "change areas")
      const a = activeArea(tx, c.areaId)
      tx.emit({ _tag: "AreaCharterRevised", areaId: a.id, charter: c.charter, before: a.charter })
      return
    }
    case "ArchiveArea": {
      personWorksInProject(tx, "change areas")
      const a = areaOf(s(), c.areaId) ?? notFound({ _tag: "area", id: c.areaId })
      if (a.status === "archived") invalid("area is already archived", "areaId")
      const open = s().items.filter((i) => i.areaId === a.id && isOpenItem(i))
      if (open.length > 0)
        invalid(
          `an area can be archived only with no open items (${open
            .slice(0, 5)
            .map((i) => i.ref)
            .join(", ")})`,
          "areaId",
        )
      tx.emit({ _tag: "AreaArchived", areaId: a.id })
      return
    }
    case "ReviseBrief": {
      // decision 0049: the only writer of the brief after genesis; an agent's applies on a person's approval
      // decision 0084 (§1.2): the brief moved into project/overview.md; the command stays so old events decode
      if (pageOf(s(), "overview"))
        invalid(
          "the brief is now project/overview.md: edit that page (people) or propose_page (agents)",
          "body",
        )
      personWorksInProject(tx, "change the brief")
      const p = must(s().project, "project")
      if (c.baseHash !== briefHash(p.description))
        invalid(
          "the brief changed since this edit was written: read it again and write the edit against it",
          "baseHash",
        )
      if (c.body.length > BRIEF_MAX_CHARS)
        invalid(`the brief is at most ${BRIEF_MAX_CHARS} characters`, "body")
      if (c.body === p.description) invalid("the brief already says this", "body")
      tx.emit({ _tag: "BriefRevised", body: c.body, before: p.description, why: c.why?.trim() || null })
      return
    }
    case "HireAgent": {
      personWorksInProject(tx, "hire agents")
      requireNew(tx, c.agentId, "agent")
      if (handleTaken(tx, c.handle, null)) invalid(`@${c.handle} is taken in this project`, "handle")
      // K17: one lead per project, set at creation (leadOf reads project.leadAgentId, which only CreateProject sets)
      if (c.role === "lead") invalid("a project has one lead; hire a role agent", "role")
      // K7: a role agent belongs to one area, or to none (project-wide)
      if (c.areaId !== null && areaOf(s(), c.areaId)?.status !== "active")
        invalid("area does not exist or is archived", "areaId")
      checkHands(c.hands, "hands")
      const policyExists = s().policies.some((p) => p.id === c.policyId)
      if (!policyExists)
        tx.emit({
          _tag: "PolicyCreated",
          policy: {
            ...tx.recordMeta(),
            id: c.policyId,
            subject: { _tag: "agent", agentId: c.agentId },
            revision: 1,
            document: PolicyPresets[c.policyPreset],
            restoredFrom: null,
          },
        })
      tx.emit({
        _tag: "AgentHired",
        agent: {
          ...tx.recordMeta(),
          id: c.agentId,
          role: c.role,
          projectId: s().project?.id ?? null,
          areaId: c.areaId,
          name: c.name,
          handle: c.handle,
          title: c.title,
          avatar: { emoji: null, color: "slate" },
          charter: c.charter,
          mind: c.mind,
          hands: c.hands ?? { agent: null, model: null, effort: null },
          policyId: c.policyId,
          wake: { debounceMs: 60_000 },
          status: "active",
        },
      })
      return
    }
    case "UpdateAgent": {
      const a = agentOf(s(), c.agentId) ?? notFound({ _tag: "agent", id: c.agentId })
      checkAgentChange(tx, a)
      if (c.patch.ownerId !== undefined) invalid("use TakeOverAgent", "patch.ownerId")
      if (c.patch.areaId !== undefined) {
        if (a.role !== "role") invalid("only role agents belong to an area", "patch.areaId")
        // K7: null = project-wide
        if (c.patch.areaId !== null && areaOf(s(), c.patch.areaId)?.status !== "active")
          invalid("area does not exist or is archived", "patch.areaId")
      }
      if (
        c.patch.handle !== undefined &&
        c.patch.handle !== a.handle &&
        handleTaken(tx, c.patch.handle, a.id)
      )
        invalid(`@${c.patch.handle} is taken in this project`, "patch.handle")
      if (c.patch.name !== undefined && (c.patch.name.trim().length === 0 || c.patch.name.length > 40))
        invalid("a name has 1–40 characters", "patch.name")
      checkHands(c.patch.hands, "patch.hands")
      // decision 0048: an absent `autonomy` means "act"; `before` never holds an undefined key (it would not encode).
      // A missing cap means no daily budget; preserve that effective value for undo.
      const before = beforeOf(a, c.patch)
      tx.emit({
        _tag: "AgentUpdated",
        agentId: a.id,
        patch: c.patch,
        before: {
          ...before,
          ...(c.patch.autonomy !== undefined ? { autonomy: levelOf(a) } : {}),
          ...(c.patch.timedCap !== undefined ? { timedCap: timedCapOf(a) } : {}),
          ...(c.patch.chainCap !== undefined ? { chainCap: a.chainCap ?? null } : {}),
        },
      })
      return
    }
    case "ReviseAgentCharter": {
      const a = liveAgent(tx, c.agentId)
      // wave C (decision 0071, 0054 D5): anyone in the project (not a viewer) edits the mission and instructions; the
      // keeper is told (Changed for you). Decision 0127: an agent's change on a person's approval is checked against
      // that person (a member may approve it); an agent on its own keeps K3's rule.
      const holder = rightsHolderOf(tx)
      if (holder !== null) {
        if (!isWorkingMember(s(), holder))
          forbidden("not_owner", "only a project member (not a viewer) edits an agent's instructions")
      } else checkAgentChange(tx, a)
      tx.emit({ _tag: "AgentCharterRevised", agentId: a.id, charter: c.charter, before: a.charter })
      return
    }
    case "ChangeAgentMind": {
      const a = liveAgent(tx, c.agentId)
      checkAgentChange(tx, a)
      tx.emit({ _tag: "AgentMindChanged", agentId: a.id, mind: c.mind, before: a.mind })
      return
    }
    case "TakeOverAgent": {
      // K3b: people only; the actor becomes the owner
      const a = liveAgent(tx, c.agentId)
      // wave E (decision 0063, gap 8): when its keeper left, membership hands the agent to the workspace owner
      if (actor._tag === "system" && actor.process === "membership") {
        if (!ownerLeft(s(), a)) forbidden("not_owner", `@${a.handle}'s owner is still a member`)
        const to = workspaceOwnerOf(s())
        if (to === null || to === a.ownerId) invalid("there is no workspace owner to hand it to", "agentId")
        tx.emit({
          _tag: "AgentUpdated",
          agentId: a.id,
          patch: { ownerId: to },
          before: { ownerId: a.ownerId },
        })
        if (c.mind) tx.emit({ _tag: "AgentMindChanged", agentId: a.id, mind: c.mind, before: a.mind })
        return
      }
      if (actor._tag !== "user") forbidden("actor_kind", "only a person takes over an agent")
      const project = s().project
      const projectOwnerAfterLeave = project?.ownerId === actor.userId && ownerLeft(s(), a)
      if (!s().members.some((m) => m.userId === actor.userId && m.role !== "viewer"))
        forbidden("not_owner", "only a member of the workspace takes over an agent")
      if (!mayManage(s(), actor.userId, a.ownerId) && !projectOwnerAfterLeave)
        forbidden(
          "not_owner",
          "a workspace owner or admin takes over an agent, or the project owner when its owner left",
        )
      if (a.ownerId === actor.userId && !c.mind) invalid(`@${a.handle} is already yours`, "agentId")
      if (a.ownerId !== actor.userId)
        tx.emit({
          _tag: "AgentUpdated",
          agentId: a.id,
          patch: { ownerId: actor.userId },
          before: { ownerId: a.ownerId },
        })
      if (c.mind) tx.emit({ _tag: "AgentMindChanged", agentId: a.id, mind: c.mind, before: a.mind })
      return
    }
    case "SetAgentStatus": {
      const a = agentOf(s(), c.agentId) ?? notFound({ _tag: "agent", id: c.agentId })
      checkAgentChange(tx, a)
      if (a.status === c.status) invalid(`agent is already ${c.status}`, "status")
      // K17
      if (c.status === "retired" && s().project?.leadAgentId === a.id)
        invalid("a project needs its lead; pause it instead", "status")
      if (a.status === "retired") invalid("a retired agent stays retired", "status")
      if (
        c.status === "retired" &&
        s().items.some((t) => t.owner?._tag === "agent" && t.owner.agentId === a.id && isOpenItem(t))
      )
        invalid("an agent can be retired only with no open items it owns", "status")
      tx.emit({ _tag: "AgentStatusChanged", agentId: a.id, status: c.status, before: a.status })
      return
    }
    // ---------------- items ----------------
    case "CreateItem": {
      requireNew(tx, c.itemId, "item")
      requireWhy(c.why, "why")
      if (c.title.trim().length === 0) invalid("an item needs a title", "title")
      const type = c.type ?? DEFAULT_ITEM_TYPE
      const def =
        typeDefOf(type) ??
        invalid(`unknown item type ${type}: one of ${ITEM_TYPES.map((t) => t.key).join(", ")}`, "type")
      const parentId = c.parentId ?? null
      const parent = parentId ? requireItem(tx, parentId) : undefined
      if (parent && isClosed(parent)) invalid(`${parent.ref} is ${parent.status}`, "parentId")
      if (parent && depthOf(s(), parent) + 1 > MAX_DEPTH)
        invalid(
          `items nest at most ${MAX_DEPTH + 1} levels: ${parent.ref} is at the deepest level`,
          "parentId",
        )
      if (parent?.recurrence)
        invalid(`${parent.ref} repeats: its occurrences are created with Run now (RunOccurrence)`, "parentId")
      // goals go on top-level items; children serve their top-level item's goals
      if (parent && (c.goalIds?.length ?? 0) > 0)
        invalid("goals go on top-level items: children serve their top-level item's goals", "goalIds")
      ;(c.goalIds ?? []).forEach((g, i) => {
        const goal = goalOf(s(), g)
        if (!goal) invalid(`goal ${g} does not exist`, `goalIds.${i}`)
        if (goal.status !== "active") invalid(`goal ${goal.ref} is not active`, `goalIds.${i}`)
      })
      const homeArea = parent ? parent.areaId : null
      const areaId = c.areaId ?? homeArea
      const area = areaId ? activeArea(tx, areaId) : undefined
      const status = c.status ?? "todo"
      if (status === "todo" && areaId === null)
        invalid("say which area it belongs to: areaId (needed from todo on)", "areaId")
      // workers create one child of their own item (no tool offers it this release), owned like the parent
      let owner: Principal | null | undefined = c.owner
      if (actor._tag === "worker") {
        if (parentId !== actor.itemId)
          forbidden("actor_kind", "a worker creates children of its own item only")
        owner = parent?.owner ?? null
      }
      let request: Item["request"] = null
      // decision 0070 (TRI-15): a person's item in an area without an intake agent needs no request: nobody there
      // would accept it, and the person was named for it. The owner is the given or derived one.
      const personsItem = c.assignee?._tag === "user" && !area?.intakeAgentId
      if (parent && area && homeArea !== null && areaId !== homeArea && !personsItem) {
        // cross-area request: the receiving area's agent decides (§7.2)
        if (!area.intakeAgentId) invalid(`area ${area.name} has no agent to accept requests`, "areaId")
        request = { fromAreaId: homeArea, state: "pending" }
        owner = { _tag: "agent", agentId: area.intakeAgentId }
      }
      // the owner: the nearest agent above (or the area's agent, or the lead), never the acting agent by default
      if (owner === undefined) {
        const above = agentAboveOf(s(), { parentId, areaId })
        owner = above ? { _tag: "agent", agentId: above.id } : null
      }
      if (owner === null && status === "todo") invalid("an item from todo on needs an owner", "owner")
      validateOwner(tx, owner, areaId, "owner")
      if (def.plannable && c.assignee !== undefined && c.assignee !== null)
        invalid(`a ${def.label.toLowerCase()} is done through its children: no assignee`, "assignee")
      const assignee = c.assignee !== undefined ? c.assignee : defaultAssignee(type, owner)
      validateAssignee(tx, assignee, owner, "assignee")
      const checks = c.checks ?? def.defaultChecks
      if (c.bet) {
        if (parent) invalid("a bet goes on a top-level item", "bet")
        if (!kpiOf(s(), c.bet.inputKpiId)) invalid("a bet must name an existing input KPI", "bet.inputKpiId")
        if (c.bet.verdict) invalid("a bet's verdict is recorded with RecordBetVerdict", "bet.verdict")
      }
      if (c.recurrence) {
        if (def.plannable)
          invalid("a plannable item doesn't repeat: put the recurring item under it", "recurrence")
        checkRecurrence(c.recurrence, "recurrence")
      }
      // captures: a person's is clean when typed into Minga (web, CLI); an agent's carries its run's taint
      let source: Item["source"] = null
      let tainted = false
      if (status === "triage") {
        if (actor._tag === "agent" || actor._tag === "worker") {
          if (c.source && c.source._tag !== "agent")
            invalid("source `agent` is exactly the captures made by an agent's turn", "source")
          source = { _tag: "agent" }
          tainted = tx.state.runs.find((r) => r.id === actor.runId)?.taint === "tainted"
        } else {
          if (c.source?._tag === "agent")
            invalid("source `agent` is exactly the captures made by an agent's turn", "source")
          source = c.source ?? { _tag: "web" }
          tainted = source._tag !== "web" && source._tag !== "cli"
        }
      } else if (c.source) invalid("`source` is for captures (status triage)", "source")
      for (const g of c.integrations ?? [])
        if (!area?.grants.some((x) => x.integrationId === g))
          invalid(`integration ${g} is not granted to ${area?.name ?? "the item's area"}`, "integrations")
      const dup = s().items.find(
        (t) =>
          isOpenItem(t) &&
          t.intentKey === c.intentKey &&
          t.parentId === parentId &&
          samePrincipal(t.owner, owner ?? null),
      )
      if (dup)
        reject({
          _tag: "Duplicate",
          existing: { _tag: "item", id: dup.id },
          reason: `an open item ${dup.ref} already has intent ${c.intentKey}`,
        })
      if (c.supersedes && !itemOf(s(), c.supersedes)) invalid("superseded item does not exist", "supersedes")
      checkHands(c.hands, "hands")
      checkRouting(tx, c.routing, def.key, "routing", c.hands)
      const workRequest = c.workRequest ?? parent?.workRequest
      if (workRequest) {
        const responsibility = s().workRequests?.find((r) => r.id === workRequest.requestId)
        if (
          !responsibility ||
          responsibility.target.projectId !== s().project?.id ||
          responsibility.revision !== workRequest.revision ||
          responsibility.state !== "pending"
        )
          invalid("use a current pending incoming responsibility revision", "workRequest")
        if (
          actor._tag === "agent" &&
          actor.agentId !== responsibility.target.agentId &&
          actor.agentId !== leadOf(s())?.id
        )
          forbidden("not_owner", "the assigned agent or lead plans this responsibility")
      }
      const item: Item = {
        ...tx.recordMeta(),
        ref: tx.nextItemRef(),
        title: c.title,
        why: c.why,
        body: c.body ?? "",
        rank: c.rank ?? siblingsRank(tx, parentId, areaId),
        hold: { _tag: "none" },
        tags: c.tags ?? [],
        id: c.itemId,
        type: def.key,
        kind: c.kind ?? def.kind,
        parentId,
        goalIds: parent ? [] : [...(c.goalIds ?? [])],
        areaId,
        owner: owner ?? null,
        assignee,
        status,
        resolution: null,
        dueAt: c.dueAt ?? null,
        notBefore: c.notBefore ?? null,
        recurrence: c.recurrence ?? null,
        checks,
        checksFrozenAt: null,
        // decision 0116: who wrote the checks (the person who approved the card, else the creator)
        checksBy: tx.writer(),
        source,
        tainted,
        bet: c.bet ?? null,
        request,
        blocked: null,
        paused: false,
        intentKey: c.intentKey,
        review: withRequiredValidator(
          defaultReview(checks, c.review),
          validatorRequired(s().project?.routing, def.key, c.routing) && assignee?._tag === "worker",
        ),
        placement: c.placement ?? { requires: {}, prefers: [], affinity: null, exclusive: [] },
        hands: c.hands ?? { agent: null, model: null, effort: null },
        routing: c.routing ?? null,
        ...(c.readOnly ? { readOnly: true } : {}),
        paths: c.paths ?? [],
        integrations: c.integrations ?? [],
        attempts: 0,
        retry: null,
        activeRunId: null,
        lastRunId: null,
        supersedes: c.supersedes ?? null,
        specRev: { n: 1 as Item["specRev"]["n"], at: tx.now, by: tx.actor },
        startedAt: null,
        closedAt: null,
        overdueNoticed: null,
        ...askedByOfCreator(tx),
        ...(workRequest ? { workRequest } : {}),
      }
      tx.emit({ _tag: "ItemCreated", item })
      // decision 0154: held for a person's go from the start (a gate card to them), so no worker starts it on its own
      if (c.goFrom) openGoGate(tx, item, c.goFrom)
      return
    }
    case "UpdateItem": {
      const t = requireItem(tx, c.itemId)
      checkHold(tx, t.hold, "edit")
      if (!isOpenItem(t)) invalid(`${t.ref} is ${t.status}`, "itemId")
      const p = c.patch
      // wave E (decision 0063): who asked and a lend are written by decide only (shaping, LendRunner)
      for (const k of MINGA_ONLY_PATCH_KEYS) if (p[k] !== undefined) invalid("set by Minga", `patch.${k}`)
      if (!Object.values(p).some((v) => v !== undefined)) invalid("the patch changes nothing", "patch")
      requireWhy(p.why, "patch.why")
      if (p.title !== undefined && p.title.trim().length === 0)
        invalid("an item needs a title", "patch.title")
      // Wave A §4.3: WIP is a warning (core `wipWarnings`), never a refusal: becoming plannable in Now is allowed
      if (p.type !== undefined && !typeDefOf(p.type))
        invalid(
          `unknown item type ${p.type}: one of ${ITEM_TYPES.map((x) => x.key).join(", ")}`,
          "patch.type",
        )
      if (p.areaId !== undefined) {
        if (t.attempts > 0) invalid("the area changes only before the first attempt", "patch.areaId")
        if (p.areaId === null && t.status !== "triage" && t.status !== "backlog")
          invalid("from todo on an item needs an area", "patch.areaId")
        if (p.areaId !== null) activeArea(tx, p.areaId, "patch.areaId")
        validateOwner(tx, t.owner, p.areaId, "patch.areaId")
      }
      if (p.goalIds !== undefined) {
        if (t.parentId !== null) invalid("goals go on top-level items", "patch.goalIds")
        p.goalIds.forEach((g, i) => {
          if (goalOf(s(), g)?.status !== "active")
            invalid(`goal ${g} is not an active goal`, `patch.goalIds.${i}`)
        })
      }
      if (p.assignee !== undefined) {
        if (p.assignee?._tag !== "worker")
          invalid(
            "UpdateItem changes a worker's preset only; use AssignItem for the owner or assignee",
            "patch.assignee",
          )
        validateAssignee(tx, p.assignee, t.owner, "patch.assignee")
      }
      if (p.tainted !== undefined) {
        if (p.tainted !== false)
          invalid("only `tainted: false` (adopt the captured text as the description)", "patch.tainted")
        if (actor._tag !== "user")
          forbidden("actor_kind", "only a person adopts captured text as the description")
      }
      if (p.bet !== undefined) {
        if (t.parentId !== null) invalid("bets go on top-level items", "patch.bet")
        checkBetPatch(tx, t.bet, p.bet)
      }
      if (p.recurrence !== undefined && p.recurrence !== null) {
        if (isPlannableType(p.type ?? t.type))
          invalid("a plannable item doesn't repeat: put the recurring item under it", "patch.recurrence")
        checkRecurrence(p.recurrence, "patch.recurrence")
      }
      const areaId = p.areaId !== undefined ? p.areaId : t.areaId
      const area = areaId ? areaOf(s(), areaId) : undefined
      for (const g of p.integrations ?? [])
        if (!area?.grants.some((x) => x.integrationId === g))
          invalid(`integration ${g} is not granted to this area`, "patch.integrations")
      checkHands(p.hands, "patch.hands")
      checkRouting(
        tx,
        p.routing !== undefined ? p.routing : t.routing,
        p.type ?? t.type,
        "patch.routing",
        p.hands ?? t.hands,
      )
      // wave E (decision 0063, spec §3.2): a change to what the next attempt reads or does moves Asked by to whoever
      // made it and ends a lend, in the same event
      const shaping = SHAPING_KEYS.some((k) => p[k] !== undefined) ? shapingPatch(s(), t, actor) : {}
      tx.emit({
        _tag: "ItemUpdated",
        itemId: t.id,
        patch: { ...p, ...shaping },
        before: {
          ...beforeOf(t, p),
          ...(p.routing !== undefined ? { routing: t.routing ?? null } : {}),
          ...(p.readOnly !== undefined ? { readOnly: t.readOnly ?? false } : {}),
          ...shapingBefore(t, shaping),
        },
      })
      // decision 0116: a new place holds for the attempt that has not started yet, not only for the one after it
      const moved =
        (p.placement !== undefined && JSON.stringify(p.placement) !== JSON.stringify(t.placement)) ||
        (p.routing !== undefined && JSON.stringify(p.routing ?? null) !== JSON.stringify(t.routing ?? null))
      if (moved)
        replaceUnstarted(tx, t, `${t.ref}'s placement changed: the next attempt runs where it now says`)
      return
    }
    case "MoveItem": {
      const t = requireItem(tx, c.itemId)
      checkHold(tx, t.hold, "move")
      if (c.parentId === undefined && c.status === undefined)
        invalid("say where it moves: parentId, status or both", "")
      // decision 0152: a closed item may be re-filed (a new parent, nothing else): its status, evidence, owner and
      // area stay as they closed. A planning status on a closed item is still refused.
      const closed = isClosed(t)
      if (closed && c.status !== undefined)
        invalid(
          `${t.ref} is ${t.status}: closed items stay closed; re-file it with parentId alone, or redo it with CreateItem{supersedes}`,
          "status",
        )
      const parentChanges = c.parentId !== undefined && c.parentId !== t.parentId
      const statusChanges = c.status !== undefined && c.status !== t.status
      let newParent: Item | undefined
      if (closed && parentChanges) refileGuards(tx, t, c.parentId ?? null)
      if (parentChanges && c.parentId) {
        newParent = requireItem(tx, c.parentId)
        // an open item under a closed parent would make the parent's closed state untrue; closed under closed is fine
        if (isClosed(newParent) && !closed)
          invalid(`${newParent.ref} is ${newParent.status}: only closed items are filed under it`, "parentId")
        if (parentCycle(s(), t.id, newParent.id))
          invalid(`${newParent.ref} is ${t.ref} or below it: that would make a cycle`, "parentId")
        if (depthOf(s(), newParent) + 1 + heightOf(tx, t.id) > MAX_DEPTH)
          invalid(
            `items nest at most ${MAX_DEPTH + 1} levels: ${t.ref}'s tree doesn't fit under ${newParent.ref}`,
            "parentId",
          )
        if (newParent.recurrence)
          invalid(`${newParent.ref} repeats: only its occurrences go under it`, "parentId")
      }
      // An item without an owner (an old initiative: v1 `ownerAgentId: null`, or a backlog item) that leaves the
      // backlog gets the agent above it, like CreateItem's default; accepting a capture from triage names its owner.
      let defaultOwner: Item["owner"] = null
      if (
        statusChanges &&
        (c.status === "todo" || c.status === "in_progress") &&
        t.owner === null &&
        t.status !== "triage"
      ) {
        const above = agentAboveOf(s(), {
          parentId: parentChanges ? (c.parentId ?? null) : t.parentId,
          areaId: t.areaId,
          id: t.id,
        })
        if (above) defaultOwner = { _tag: "agent", agentId: above.id }
      }
      if (statusChanges && c.status !== undefined) {
        const to = c.status
        const live = t.activeRunId !== null
        const topAfter = parentChanges ? c.parentId === null : t.parentId === null
        const containerAfter = isContainer(s(), t)
        const plannableTop = topAfter && isPlannable(t)
        const personLeaf = !containerAfter && t.assignee?._tag === "user"
        // decision 0084 (§1.5) said nothing may be in flight under an item moving to Planning. Decision 0094 relaxes it:
        // Planning stops new dispatch under the item (readiness); a run already leased or in review finishes on its own
        // lease, and the log keeps saying what it did. Only the item's own live run is refused below.
        if (to === "triage") {
          if (actor._tag !== "user")
            forbidden(
              "actor_kind",
              `only a person moves an item back to triage: move ${t.ref} to Planning (status backlog) instead`,
            )
          if (t.status !== "backlog" && t.status !== "todo")
            invalid(`${t.ref} is ${t.status}: only backlog and todo items go back to triage`, "status")
          if (t.attempts > 0 || t.startedAt !== null)
            invalid(`${t.ref} has started: it stays out of triage`, "status")
        } else if (to === "backlog" || to === "todo") {
          // decision 0120: an item in review names the next step for the stage it waits at
          if (t.status === "in_review") invalid(inReviewNextStep(s(), t), "status")
          if (!["triage", "backlog", "todo", "in_progress"].includes(t.status))
            invalid(
              t.status === "blocked"
                ? `${t.ref} is blocked: use retry_item, unblock_item or close_item for it`
                : `${t.ref} is ${t.status}: closed items stay closed; redo it with CreateItem{supersedes}`,
              "status",
            )
          // decision 0094: kept (a live run under Planning or Ready would misstate what runs), with what to do instead
          if (live)
            invalid(
              `${t.ref} has a live run (${runOf(s(), t.activeRunId as never)?.ref ?? "its run"}): stop_run it first, or let it finish. Its parent, text, checks, owner and rank can change while it runs`,
              "status",
            )
          if (
            to === "todo" &&
            ((t.owner ?? defaultOwner) === null || (parentChanges ? newParent?.areaId : t.areaId) === null)
          )
            invalid(`${t.ref} needs an owner and an area to be planned (todo)`, "status")
        } else {
          // in_progress: planning "Now" for containers and plannable top-level items; "I'm on it" for a person
          if (t.recurrence !== null) invalid(`${t.ref} repeats: Run now starts an occurrence`, "status")
          if (!containerAfter && !plannableTop && !personLeaf)
            invalid(
              t.assignee?._tag === "worker"
                ? `${t.ref} is done by a worker: a worker starts it when it is ready`
                : `only containers, top-level plannable items and a person's own items move to in_progress`,
              "status",
            )
          if (personLeaf && actor._tag !== "user")
            forbidden("actor_kind", `${t.ref} is a person's to start ("I'm on it")`)
          if (t.status === "in_review") invalid(inReviewNextStep(s(), t), "status")
          if (!["triage", "backlog", "todo"].includes(t.status))
            invalid(`${t.ref} is ${t.status}: it moves to Now from Later or Next`, "status")
          // Wave A §4.3: more than the usual count in Now is a warning (`wipWarnings`), never a refusal
        }
      }
      if (!parentChanges && !statusChanges) return // already there: Applied, no event
      if (parentChanges) {
        const parentId = c.parentId ?? null
        tx.emit({
          _tag: "ItemMoved",
          itemId: t.id,
          parentId,
          before: t.parentId,
          goalIds: parentId === null ? t.goalIds : [],
          beforeGoalIds: t.goalIds,
        })
      }
      if (defaultOwner !== null)
        tx.emit({ _tag: "ItemAssigned", itemId: t.id, owner: defaultOwner, before: null })
      if (statusChanges && c.status !== undefined) {
        const cur = must(itemOf(s(), t.id), "item")
        tx.emit({
          _tag: "ItemStatusChanged",
          itemId: t.id,
          from: cur.status,
          to: c.status,
          cause: { _tag: "planned" },
          blocked: null,
          resolution: null,
        })
      }
      return
    }
    case "AssignItem": {
      const t = requireItem(tx, c.itemId)
      checkHold(tx, t.hold, "move")
      if (!isOpenItem(t)) invalid(`${t.ref} is ${t.status}`, "itemId")
      if (c.owner === undefined && c.assignee === undefined) invalid("say who: owner, assignee or both", "")
      const owner = c.owner !== undefined ? c.owner : t.owner
      if (owner === null && t.status !== "triage" && t.status !== "backlog")
        invalid("from todo on an item needs an owner", "owner")
      validateOwner(tx, owner, t.areaId, "owner")
      if (isPlannable(t) && c.assignee !== undefined && c.assignee !== null)
        invalid(
          `a ${typeDefOf(t.type)?.label.toLowerCase() ?? "plannable item"} is done through its children: no assignee`,
          "assignee",
        )
      let assignee = c.assignee !== undefined ? c.assignee : t.assignee
      // a new owner that can't keep the current assignee (a worker needs an agent owner) gets the default one
      if (c.assignee === undefined && assignee?._tag === "worker" && owner?._tag !== "agent")
        assignee = defaultAssignee(t.type, owner)
      validateAssignee(tx, assignee, owner, "assignee")
      const ownerChanged = !samePrincipal(owner, t.owner)
      const assigneeChanged = !same(assignee, t.assignee)
      // decision 0094: already so is not an error (Applied, no event), like MoveItem
      if (!ownerChanged && !assigneeChanged) return
      tx.emit({
        _tag: "ItemAssigned",
        itemId: t.id,
        owner,
        before: t.owner,
        ...(assigneeChanged ? { assignee, beforeAssignee: t.assignee } : {}),
      })
      // wave E (spec §3.2, §4.1): who does the next attempt changed, so the assigner shapes it (an agent in a turn:
      // the turn's asker)
      emitShaping(tx, t.id)
      return
    }
    case "ReviseChecks": {
      const t = requireItem(tx, c.itemId)
      if (actor._tag === "worker") forbidden("frozen_contract", "workers can never change done checks")
      checkHold(tx, t.hold, "edit")
      if (!isOpenItem(t)) invalid(`${t.ref} is ${t.status}`, "itemId")
      const review = defaultReview(c.checks, c.review ?? t.review)
      // decision 0120: the index follows the stages it already passed, so it never points past the new stages or at a
      // different stage than the one the item waits at
      const at = remapReviewStage(t.review, review.stages)
      const was = reviewStageOf(t)
      if (t.status === "in_review" && at >= review.stages.length)
        invalid(
          `${t.ref} is in review and has passed ${t.review.stages.slice(0, t.review.stage).join(", ") || "no stage"}: these stages leave none ahead of it. Keep a stage it has not passed (agent, so its owner approves it with review), or approve it at its current stage`,
          "review",
        )
      const stage = at < review.stages.length ? at : 0
      tx.emit({
        _tag: "ChecksRevised",
        itemId: t.id,
        checks: c.checks,
        review: {
          ...review,
          round: t.review.round,
          stage,
          // decision 0050: the person rounds so far stay outside maxRounds
          ...(t.review.personRounds !== undefined ? { personRounds: t.review.personRounds } : {}),
        },
        before: { checks: t.checks, review: t.review },
        by: tx.writer(),
      })
      // wave E (spec §3.2): new checks shape the next attempt (an ItemUpdated{ askedBy } in the same transaction)
      emitShaping(tx, t.id)
      if (t.status === "in_review") leaveRemovedStage(tx, t, was)
      return
    }
    case "CloseItem": {
      const t = requireItem(tx, c.itemId)
      checkHold(tx, t.hold, "edit")
      if (c.note.trim().length === 0) invalid("say why: note must not be blank", "note")
      if (!isOpenItem(t)) invalid(`${t.ref} is already ${t.status}`, "itemId")
      if (c.as === "cancelled") {
        if (c.result !== undefined) invalid("a result is for items closed as done", "result")
        const resolution: Resolution = c.resolution ?? { _tag: "wont_do" }
        if (resolution._tag === "blocked_external" && resolution.what.trim().length === 0)
          invalid("name what blocks it from outside", "resolution.what")
        if (resolution._tag === "duplicate" || resolution._tag === "superseded") {
          const other = resolution._tag === "duplicate" ? resolution.of : resolution.by
          if (other === t.id) invalid(`${t.ref} can't point at itself`, "resolution")
          if (!itemOf(s(), other)) notFound({ _tag: "item", id: other })
        }
        cancelCascade(tx, t, c.note, resolution)
        return
      }
      if (c.resolution) invalid("a resolution is for cancelled items", "resolution")
      // decision 0101: a person who closes an item in review without new evidence closes it on the evidence it has
      const evidence =
        (c.evidence?.length ?? 0) === 0 && t.status === "in_review" && personsCloseBy(tx) !== null
          ? doneEvidence(s(), t)
          : (c.evidence ?? [])
      if (isContainer(s(), t)) {
        const open = childrenOf(s(), t.id).filter(isOpenItem)
        if (open.length > 0)
          invalid(
            `${open.length} ${open.length === 1 ? "child" : "children"} of ${t.ref} ${open.length === 1 ? "is" : "are"} open (${open
              .slice(0, 5)
              .map((x) => x.ref)
              .join(", ")}): close them first, or cancel ${t.ref}`,
            "itemId",
          )
        if (t.checks.length === 0) {
          if (t.kind === "publish") {
            const gap = checksEvidenceGap(s(), t, evidence, { strict: true })
            if (gap) invalid(gap.detail, "evidence")
          }
          if (t.paused) mayResume(tx, t, "close")
          if (t.paused) tx.emit({ _tag: "ItemPaused", itemId: t.id, paused: false, by: principalOf(actor) })
          tx.emit({
            _tag: "ItemStatusChanged",
            itemId: t.id,
            from: t.status,
            to: "done",
            cause: { _tag: "closed", note: c.note, evidence, ...(c.result ? { result: c.result } : {}) },
            blocked: null,
            resolution: null,
          })
          return
        }
      }
      // decision 0101 (S7): a person's close, or one a person approved, is the final review; a person who did the work
      // (the assignee of an item an agent owns) reports it done instead, and the owner reviews it as before. A question
      // is answered, not worked: the answer of the person it asks is final (decision 0170)
      const closer = personsCloseBy(tx)
      const doer =
        t.type !== "question" &&
        actor._tag === "user" &&
        t.assignee?._tag === "user" &&
        t.assignee.userId === actor.userId &&
        t.owner?._tag === "agent"
      const byPerson = doer ? null : closer
      // a leaf, or a container with checks: through its review stages (invariant 6)
      if (t.activeRunId !== null)
        invalid(
          `${t.ref} has a live run: stop_run it first, then close it (closing it as cancelled stops the run too)`,
          "itemId",
        )
      if (t.status === "in_review" && byPerson === null) invalid(inReviewNextStep(s(), t), "itemId")
      if (t.request?.state === "pending")
        invalid(`${t.ref} is a cross-area request: it must be accepted first`, "itemId")
      // closing lifts the pause, so it needs the right to resume: a person's pause wins over any agent
      if (t.paused) mayResume(tx, t, "close")
      mayPassEscalation(tx, t, "close")
      const needsEvidence =
        t.kind === "publish" || t.checks.some((x) => x._tag === "command" || x._tag === "artifact")
      if (needsEvidence || evidence.length > 0) {
        const gap = checksEvidenceGap(s(), t, evidence, { strict: true })
        if (gap) invalid(gap.detail, "evidence")
      }
      evidence.forEach((e, i) => {
        if (e._tag !== "run") return
        const r = runOf(s(), e.runId)
        if (!r) invalid(`run ${e.runId} does not exist`, `evidence[${i}]`)
        if (r.itemId !== t.id) invalid(`${r.ref} is not a run of ${t.ref}`, `evidence[${i}]`)
      })
      if (byPerson !== null) {
        if (t.paused) tx.emit({ _tag: "ItemPaused", itemId: t.id, paused: false, by: principalOf(actor) })
        closeByPerson(tx, t, byPerson, c.note, evidence, c.result)
        return
      }
      if (t.review.stages.includes("validator") && t.lastRunId === null)
        invalid(`${t.ref} has a validator stage and no worker run to validate; ask a person`, "itemId")
      withdrawEscalations(tx, t.id)
      // a closed item is never shown paused
      if (t.paused) tx.emit({ _tag: "ItemPaused", itemId: t.id, paused: false, by: principalOf(actor) })
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: t.status,
        to: "in_review",
        cause: { _tag: "closed", note: c.note, evidence, ...(c.result ? { result: c.result } : {}) },
        blocked: null,
        resolution: null,
      })
      // later review stages still run (invariant 6); the closer stands in for the agent stage only when it is the
      // owner, the lead, or a person on a person-owned item (otherwise the owner agent reviews)
      const cur = must(itemOf(s(), t.id), "item")
      if (cur.review.stages[cur.review.stage] === "agent" && closerStandsIn(tx, cur)) {
        tx.emit({
          _tag: "ReviewRecorded",
          itemId: t.id,
          round: cur.review.round,
          stage: "agent",
          verdict: "approve",
          notes: c.note,
          findings: [],
          runId: null,
        })
        passStage(tx, t.id, "approve")
      } else enterCurrentStage(tx, t.id)
      return
    }
    case "RunOccurrence": {
      // Run now (D4): a person's (or an allowed agent's) command; nothing creates occurrences on a schedule
      const t = requireItem(tx, c.itemId)
      const r = t.recurrence ?? invalid(`${t.ref} doesn't repeat: give it a schedule first`, "itemId")
      if (!isOpenItem(t)) invalid(`${t.ref} is ${t.status}`, "itemId")
      if (t.paused) invalid(`${t.ref} is paused: resume it first`, "itemId")
      const open = openOccurrenceOf(s(), t)
      if (open) invalid(`${open.ref} is open: close it first`, "itemId")
      if (depthOf(s(), t) + 1 > MAX_DEPTH)
        invalid(`${t.ref} is at the deepest level: no occurrence fits under it`, "itemId")
      requireNew(tx, c.occurrenceId, "item")
      const period = periodOf(tx.now, r.schedule.tz)
      const n = childrenOf(s(), t.id).filter((x) => x.intentKey.startsWith(`occ-${period}-`)).length + 1
      const item: Item = {
        ...tx.recordMeta(),
        ref: tx.nextItemRef(),
        title: occurrenceTitle(r.title, t.title, period),
        why: t.why,
        body: "",
        rank: siblingsRank(tx, t.id, t.areaId),
        hold: { _tag: "none" },
        tags: t.tags,
        id: c.occurrenceId,
        type: t.type,
        kind: t.kind,
        parentId: t.id,
        goalIds: [],
        areaId: t.areaId,
        owner: t.owner,
        assignee: t.assignee,
        status: "todo",
        resolution: null,
        dueAt: r.window === null ? null : tx.now + r.window,
        notBefore: null,
        recurrence: null,
        checks: t.checks,
        checksFrozenAt: null,
        checksBy: t.checksBy !== undefined ? t.checksBy : principalOf(t.createdBy),
        source: null,
        tainted: false,
        bet: null,
        request: null,
        blocked: null,
        paused: false,
        intentKey: `occ-${period}-${n}` as Item["intentKey"],
        review: { ...withoutPersonRounds(t.review), round: 0, stage: 0 },
        placement: t.placement,
        hands: t.hands,
        routing: t.routing ?? null,
        ...(t.readOnly ? { readOnly: true } : {}),
        paths: t.paths,
        integrations: t.integrations,
        attempts: 0,
        retry: null,
        activeRunId: null,
        lastRunId: null,
        supersedes: null,
        specRev: { n: 1 as Item["specRev"]["n"], at: tx.now, by: tx.actor },
        startedAt: null,
        closedAt: null,
        overdueNoticed: null,
        ...askedByOfCreator(tx),
      }
      tx.emit({ _tag: "ItemCreated", item })
      return
    }
    case "DecideRequest": {
      const t = requireItem(tx, c.itemId)
      if (t.request?.state !== "pending") invalid("the item has no pending area request", "itemId")
      tx.emit({ _tag: "RequestDecided", itemId: t.id, accepted: c.accept, note: c.note ?? null })
      if (!c.accept)
        cancelCascade(tx, must(itemOf(s(), t.id), "item"), `request declined${c.note ? `: ${c.note}` : ""}`)
      return
    }
    case "Reprioritize": {
      const ref = c.ref
      let before: string
      if (ref._tag === "goal") {
        const g = goalOf(s(), ref.id) ?? notFound({ _tag: "goal", id: ref.id })
        checkHold(tx, g.hold, "move")
        before = g.rank
      } else {
        const t = requireItem(tx, ref.id)
        checkHold(tx, t.hold, "move")
        before = t.rank
      }
      tx.emit({ _tag: "RankChanged", ref, rank: c.rank, before })
      return
    }
    // ---------------- edges ----------------
    case "AddEdge": {
      requireNew(tx, c.edgeId, "edge")
      if (!workExists(s(), c.from)) notFound({ _tag: "item", id: c.from.id })
      if (!workExists(s(), c.to)) notFound({ _tag: "item", id: c.to.id })
      if (c.from.id === c.to.id) invalid("an edge never points from an item to itself", "to")
      if (s().edges.some((e) => e.kind === c.kind && e.from.id === c.from.id && e.to.id === c.to.id))
        invalid(`a ${c.kind} edge between these items exists`, "to")
      if (c.kind === "blocks") {
        const cycle = blocksCycle(s(), c.from, c.to)
        if (cycle) invalid(cycleMessage(s(), c.from.id, c.to.id, cycle), "to")
      }
      if (c.interfaceKey) {
        const fromArea = itemOf(s(), c.from.id)?.areaId ?? null
        const area = fromArea ? areaOf(s(), fromArea) : undefined
        if (!area?.interfaces.publishes.some((p) => p.key === c.interfaceKey))
          invalid(`interface ${c.interfaceKey} is not published by the source area`, "interfaceKey")
      }
      tx.emit({
        _tag: "EdgeAdded",
        edge: {
          ...tx.recordMeta(),
          id: c.edgeId,
          kind: c.kind,
          from: c.from,
          to: c.to,
          interfaceKey: c.interfaceKey ?? null,
          note: c.note ?? null,
          hold: { _tag: "none" },
        },
      })
      // decision 0041: a gate added after the item exists holds it (runs that have not started are cancelled)
      if (c.kind === "blocks") {
        const from = resolvePredecessor(s(), c.from.id as ItemId)
        if (from && from.status !== "done") holdForGate(tx, [c.to.id as ItemId], `held by ${from.ref}`)
      }
      return
    }
    case "RemoveEdge": {
      const e = edgeOf(s(), c.edgeId) ?? notFound({ _tag: "edge", id: c.edgeId })
      checkHold(tx, e.hold, "edit")
      tx.emit({ _tag: "EdgeRemoved", edgeId: e.id, edge: e })
      return
    }
    case "LegacyChange":
      // D10: a pending v1 change with no 1:1 equivalent; never applied
      invalid(`proposed before the work-model update: propose it again (${c.summary})`, "")
  }
}

/** Ids a graph change creates (other changes in the same batch may depend on them). */
export const createdIds = (c: GraphCommand): ReadonlyArray<string> => {
  switch (c._tag) {
    case "CreateGoal":
      return [c.goalId]
    case "DefineKpi":
      return [c.kpiId]
    case "CreateArea":
      return [c.areaId]
    case "HireAgent":
      return [c.agentId, c.policyId]
    case "CreateItem":
      return [c.itemId]
    case "RunOccurrence":
      return [c.occurrenceId]
    case "AddEdge":
      return [c.edgeId]
    default:
      return []
  }
}

/** Every string leaf of a change: a change depends on another if it mentions an id the other creates. */
const stringsOf = (v: unknown, out: Set<string>): Set<string> => {
  if (typeof v === "string") out.add(v)
  else if (Array.isArray(v)) for (const x of v) stringsOf(x, out)
  else if (v !== null && typeof v === "object") for (const x of Object.values(v)) stringsOf(x, out)
  return out
}

/** Direct dependencies of each change on earlier changes in the same batch. */
export const dependencies = (changes: ReadonlyArray<GraphCommand>): ReadonlyArray<ReadonlyArray<number>> =>
  changes.map((c, j) => {
    const mentioned = stringsOf(c, new Set())
    const own = new Set(createdIds(c))
    const deps: number[] = []
    for (let i = 0; i < changes.length; i++) {
      if (i === j) continue
      if (createdIds(must(changes[i], "change")).some((id) => mentioned.has(id) && !own.has(id))) deps.push(i)
    }
    return deps
  })

/**
 * Partial approval (§9.6): the selected indices plus everything they depend on must all be selected.
 * Returns the sorted selection, or the missing indices.
 */
export const dependencyClosure = (
  changes: ReadonlyArray<GraphCommand>,
  selection: ReadonlyArray<number>,
): { readonly ok: ReadonlyArray<number> } | { readonly missing: ReadonlyArray<number> } => {
  const deps = dependencies(changes)
  const sel = new Set(selection.filter((i) => i >= 0 && i < changes.length))
  const missing = new Set<number>()
  const visit = (i: number, seen: Set<number>) => {
    for (const d of deps[i] ?? []) {
      if (seen.has(d)) continue
      seen.add(d)
      if (!sel.has(d)) missing.add(d)
      visit(d, seen)
    }
  }
  for (const i of sel) visit(i, new Set([i]))
  return missing.size > 0
    ? { missing: [...missing].sort((a, b) => a - b) }
    : { ok: [...sel].sort((a, b) => a - b) }
}
